import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { startSession, fetchScreenshot, clickAt, fillAt, waitStep, extractTable, undoStep, clearSteps, closeSession, getRecipe, createRecipe, updateRecipe } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Play, Save, RotateCcw, Trash2, Download, RefreshCw, Globe } from "lucide-react";
import { toast } from "sonner";
import ModeSwitcher from "@/components/recorder/ModeSwitcher";
import RecorderCanvas from "@/components/recorder/RecorderCanvas";
import StepsTimeline from "@/components/recorder/StepsTimeline";
import SessionPill from "@/components/session/SessionPill";
import RecorderGuide, { RecorderTemplates } from "@/components/recorder/RecorderGuide";
import RecorderTour from "@/components/recorder/RecorderTour";
import { Switch } from "@/components/ui/switch";

const TOUR_STEPS = [
  {
    selector: '[data-testid="target-url-input"]',
    title: "Cola o URL alvo",
    body: "Esta é a página que vais querer scrappar. Pode ser um site público OU uma página de login — qualquer URL acessível por browser.",
    placement: "bottom",
  },
  {
    selector: '[data-testid="start-session-button"]',
    title: "Inicia uma sessão de browser",
    body: "Clica aqui para abrir uma instância Playwright real. Vais ver a captura de ecrã ao vivo no canvas em baixo. A sessão fica ativa enquanto gravas.",
    placement: "bottom",
  },
  {
    selector: '[data-testid="recorder-mode-switcher"]',
    title: "Escolhe um modo de acção",
    body: "Clicar → botões/links. Preencher → inputs (email, password, datas). Esperar → pausa entre passos. Extrair → seleciona a tabela final.",
    placement: "bottom",
  },
  {
    selector: '[data-testid="recorder-canvas"]',
    title: "Age sobre a captura de ecrã",
    body: "Clica nos elementos da captura de ecrã ao vivo para gerar passos. Cada acção cria um marcador numerado e fica gravada na timeline à direita.",
    placement: "top",
  },
  {
    selector: '[data-testid="recorder-save-recipe-button"]',
    title: "Guarda a receita quando terminares",
    body: "Dá-lhe um nome descritivo. A partir daí podes correr/agendar/exportar em CSV/JSON/XLSX desde a página Receitas.",
    placement: "left",
  },
];

const MODE_HINTS = {
  click: {
    title: "Modo Clicar",
    body: "Clica na captura de ecrã em cima de qualquer elemento clicável (botão, link, linha, separador). Cada clique gera um passo numerado.",
    color: "border-[hsl(var(--step-click))]/40 bg-[hsl(var(--step-click))]/5",
  },
  fill: {
    title: "Modo Preencher",
    body: "Clica num campo na captura de ecrã (ex: email, password, data) e aparece uma caixa para escreveres o valor. Para datepickers, edita o passo gravado e marca \"é data\".",
    color: "border-[hsl(var(--step-fill))]/40 bg-[hsl(var(--step-fill))]/5",
  },
  wait: {
    title: "Modo Esperar",
    body: "Define o tempo em ms (1000 = 1 segundo) e clica em \"Adicionar passo de espera\". Usa entre login e navegação para a página carregar.",
    color: "border-[hsl(var(--step-wait))]/40 bg-[hsl(var(--step-wait))]/5",
  },
  extract: {
    title: "Modo Extrair",
    body: "Clica numa célula da tabela final OU usa \"Extrair tabela maior\" para capturar automaticamente a maior tabela da página. Devias ter este como último passo.",
    color: "border-[hsl(var(--step-extract))]/40 bg-[hsl(var(--step-extract))]/5",
  },
};

export default function RecorderPage() {
  const { recipeId } = useParams();
  const nav = useNavigate();

  const [url, setUrl] = useState("https://example.com");
  const [sessionId, setSessionId] = useState(null);
  const [status, setStatus] = useState('idle');
  const [screenshot, setScreenshot] = useState(null);
  const [liveUrl, setLiveUrl] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [steps, setSteps] = useState([]);
  const [mode, setMode] = useState('click');
  const [busy, setBusy] = useState(false);
  const [waitMs, setWaitMs] = useState(1000);
  const [recipeName, setRecipeName] = useState("");
  const [recipeNotes, setRecipeNotes] = useState("");
  const [convertTimes, setConvertTimes] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [fillOpen, setFillOpen] = useState(false);
  const [fillCoords, setFillCoords] = useState(null);
  const [fillValue, setFillValue] = useState("");
  const [previewRows, setPreviewRows] = useState(null);
  const [tourOpen, setTourOpen] = useState(false);
  const sessionRef = useRef(null);
  const autoRefreshRef = useRef(null);

  // Auto-open tour on first visit (per browser)
  useEffect(() => {
    if (recipeId) return; // não em modo editar
    try {
      const seen = window.localStorage.getItem("vs_recorder_tour_seen");
      if (!seen) {
        // pequeno delay para o layout estabilizar
        const t = setTimeout(() => setTourOpen(true), 600);
        return () => clearTimeout(t);
      }
    } catch (_) { /* ignore */ }
  }, [recipeId]);

  function openTour() { setTourOpen(true); }
  function closeTour() {
    setTourOpen(false);
    try { window.localStorage.setItem("vs_recorder_tour_seen", "1"); } catch (_) { /* ignore */ }
  }

  function applyTemplate(t) {
    if (sessionId) return toast.error("Fecha a sessão ativa antes de aplicar um modelo");
    if (steps.length && !window.confirm("Isto substitui os passos atuais. Continuar?")) return;
    setUrl(t.payload.url || url);
    const tplSteps = (t.payload.steps || []).map((s, i) => ({
      id: `tpl_${Date.now()}_${i}`,
      ...s,
    }));
    setSteps(tplSteps);
    toast.success(`Template "${t.label}" aplicado — ajusta os valores e inicia uma sessão.`);
  }

  // Load existing recipe in edit mode
  useEffect(() => {
    if (!recipeId) return;
    (async () => {
      try {
        const r = await getRecipe(recipeId);
        setEditingRecipe(r);
        setUrl(r.start_url);
        setSteps(r.steps || []);
        setRecipeName(r.name);
        setRecipeNotes(r.notes || "");
        setConvertTimes(!!r.convert_times);
      } catch (e) { toast.error("Falha ao carregar a receita"); }
    })();
  }, [recipeId]);

  // Cleanup session on unmount
  useEffect(() => {
    return () => { if (sessionRef.current) closeSession(sessionRef.current).catch(()=>{}); };
  }, []);

  async function refreshScreenshot(sid = sessionId) {
    if (!sid) return;
    try {
      const s = await fetchScreenshot(sid);
      setScreenshot(s.image);
      if (s.viewport?.url) setLiveUrl(s.viewport.url);
    } catch (e) {
      // silent — keep previous screenshot
    }
  }

  // Optional auto-refresh of the screenshot (helps see slow navigations)
  useEffect(() => {
    if (!autoRefresh || !sessionId) {
      if (autoRefreshRef.current) { clearInterval(autoRefreshRef.current); autoRefreshRef.current = null; }
      return;
    }
    autoRefreshRef.current = setInterval(() => { refreshScreenshot(sessionId); }, 2000);
    return () => { if (autoRefreshRef.current) clearInterval(autoRefreshRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, sessionId]);

  async function onStart() {
    if (!url) return toast.error("Indique primeiro um URL");
    setBusy(true); setStatus('loading');
    try {
      const s = await startSession(url);
      setSessionId(s.session_id); sessionRef.current = s.session_id;
      setStatus('ready');
      await refreshScreenshot(s.session_id);
      toast.success("Sessão iniciada");
    } catch (e) {
      setStatus('error');
      toast.error(`Falha ao iniciar sessão: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  }

  async function onCanvasClick({ x, y, mode: m }) {
    if (!sessionId) return toast.error("Inicie primeiro uma sessão");
    setBusy(true);
    try {
      if (m === 'click') {
        const res = await clickAt(sessionId, x, y);
        setSteps(res.steps);
        await refreshScreenshot();
      } else if (m === 'fill') {
        setFillCoords({ x, y }); setFillValue(""); setFillOpen(true);
      } else if (m === 'extract') {
        const res = await extractTable(sessionId, x, y);
        setSteps(res.steps);
        setPreviewRows(res.data);
        toast.success(`Extraídas ${res.data?.rows?.length || 0} linhas`);
      }
    } catch (e) {
      toast.error(`Falha na ação: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  }

  async function onFillConfirm() {
    if (!sessionId || !fillCoords) return;
    setBusy(true); setFillOpen(false);
    try {
      const res = await fillAt(sessionId, fillCoords.x, fillCoords.y, fillValue);
      setSteps(res.steps);
      await refreshScreenshot();
    } catch (e) {
      toast.error(`Falha ao preencher: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); setFillCoords(null); setFillValue(""); }
  }

  async function onWait() {
    if (!sessionId) return toast.error("Inicie primeiro uma sessão");
    setBusy(true);
    try { const res = await waitStep(sessionId, waitMs); setSteps(res.steps); } catch (e) { toast.error("Falha ao adicionar espera"); } finally { setBusy(false); }
  }

  async function onExtractWhole() {
    if (!sessionId) return toast.error("Inicie primeiro uma sessão");
    setBusy(true);
    try {
      const res = await extractTable(sessionId, null, null);
      setSteps(res.steps);
      setPreviewRows(res.data);
      toast.success(`Extraídas ${res.data?.rows?.length || 0} linhas`);
    } catch (e) {
      toast.error(`Falha ao extrair: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  }

  async function onUndo() {
    if (!sessionId) {
      setSteps(prev => prev.slice(0, -1));
      return;
    }
    setBusy(true);
    try { const res = await undoStep(sessionId); setSteps(res.steps); } catch (e) { toast.error("Falha ao desfazer"); } finally { setBusy(false); }
  }
  async function onClearSteps() {
    if (!sessionId) { setSteps([]); return; }
    setBusy(true);
    try { const res = await clearSteps(sessionId); setSteps(res.steps); } catch (e) { toast.error("Falha ao limpar"); } finally { setBusy(false); }
  }
  function onDeleteStep(idx) {
    setSteps(prev => prev.filter((_, i) => i !== idx));
  }
  function onUpdateStep(idx, newStep) {
    setSteps(prev => prev.map((s, i) => i === idx ? newStep : s));
  }
  function onMoveStep(idx, direction) {
    setSteps(prev => {
      const next = [...prev];
      const target = idx + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  }
  function onAddStep(step) {
    setSteps(prev => [...prev, step]);
  }

  async function onSave() {
    if (!recipeName.trim()) return toast.error("Dê um nome à receita");
    if (!steps.length) return toast.error("Grave pelo menos um passo antes de guardar");
    setBusy(true);
    try {
      const payload = { name: recipeName.trim(), start_url: url, steps, notes: recipeNotes, convert_times: convertTimes, schedule: editingRecipe?.schedule || null };
      const saved = editingRecipe ? await updateRecipe(editingRecipe.id, payload) : await createRecipe(payload);
      toast.success(`Receita ${editingRecipe ? 'atualizada' : 'guardada'}`);
      setSaveOpen(false);
      nav('/');
    } catch (e) {
      toast.error(`Falha ao guardar: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  }

  async function onCloseSession() {
    if (!sessionId) return;
    await closeSession(sessionId); sessionRef.current = null;
    setSessionId(null); setScreenshot(null); setStatus('closed');
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1500px] mx-auto space-y-5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{fontFamily:'Space Grotesk'}}>{editingRecipe ? 'Editar receita' : 'Nova gravação'}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Abre um URL, clica nos botões da captura de ecrã para gravar passos, e termina com um passo "Extrair".</p>
        </div>
        <SessionPill status={status}/>
      </div>

      {/* Guia "Como funciona" — colapsável; inicia aberto se ainda não há sessão e não estamos a editar */}
      <RecorderGuide initiallyOpen={!sessionId && !editingRecipe} onStartTour={openTour}/>

      {/* Toolbar */}
      <Card className="p-4 flex flex-col gap-3">
        {/* Row 1 — URL + Start/Close (flex-wrap to keep everything inside the card) */}
        <div className="flex flex-wrap items-center gap-2">
          <Input
            data-testid="target-url-input"
            placeholder="https://example.com"
            value={url}
            onChange={e => setUrl(e.target.value)}
            className="mono text-sm flex-1 min-w-[240px]"
          />
          <Button data-testid="start-session-button" onClick={onStart} disabled={busy || !!sessionId}>
            <Play className="h-4 w-4 mr-1"/> {sessionId ? 'Sessão activa' : 'Iniciar sessão'}
          </Button>
          {sessionId && (
            <Button variant="outline" onClick={onCloseSession} data-testid="close-session-button">Fechar</Button>
          )}
        </div>

        {/* Templates rápidos — só visíveis quando ainda não há sessão e estamos a criar */}
        {!sessionId && !editingRecipe && (
          <RecorderTemplates onApply={applyTemplate}/>
        )}

        {sessionId && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Globe className="h-3.5 w-3.5 text-emerald-600 shrink-0"/>
              <span className="text-muted-foreground shrink-0">URL actual:</span>
              <span className="mono truncate text-foreground" data-testid="recorder-live-url" title={liveUrl || ""}>
                {liveUrl || "—"}
              </span>
            </div>
            <div className="flex items-center gap-3 sm:border-l sm:border-border sm:pl-3">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => refreshScreenshot()} disabled={busy} data-testid="recorder-refresh-screenshot-button" title="Forçar nova captura do ecrã sem registar passo">
                <RefreshCw className={`h-3 w-3 mr-1 ${busy ? "animate-spin" : ""}`}/> Refresh
              </Button>
              <label className="flex items-center gap-1.5 text-muted-foreground cursor-pointer">
                <Switch checked={autoRefresh} onCheckedChange={setAutoRefresh} data-testid="recorder-auto-refresh-switch"/>
                <span>Auto-refresh 2s</span>
              </label>
            </div>
          </div>
        )}

        {/* Row 2 — Modes (left) + mode extras (middle) + actions Undo/Clear/Update (right) */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground mr-1 shrink-0">Modo:</span>
          <ModeSwitcher mode={mode} onChange={setMode} disabled={!sessionId}/>

          {mode === 'wait' && (
            <div className="flex items-center gap-2">
              <Input type="number" min={100} max={30000} step={100} value={waitMs} onChange={e => setWaitMs(Number(e.target.value))} className="h-9 w-24" data-testid="wait-ms-input"/>
              <Button size="sm" variant="outline" onClick={onWait} disabled={!sessionId || busy} data-testid="add-wait-step-button">Adicionar passo de espera</Button>
            </div>
          )}
          {mode === 'extract' && (
            <Button size="sm" variant="outline" onClick={onExtractWhole} disabled={!sessionId || busy} data-testid="extract-whole-page-button">
              <Download className="h-3.5 w-3.5 mr-1"/> Extrair tabela maior
            </Button>
          )}

          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={onUndo} disabled={!steps.length || busy} data-testid="recorder-undo-button">
              <RotateCcw className="h-3.5 w-3.5 mr-1"/> Anular último
            </Button>
            <Button size="sm" variant="outline" onClick={onClearSteps} disabled={!steps.length || busy} data-testid="recorder-clear-button">
              <Trash2 className="h-3.5 w-3.5 mr-1"/> Limpar
            </Button>
            <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
              <DialogTrigger asChild>
                <Button data-testid="recorder-save-recipe-button" disabled={!steps.length}>
                  <Save className="h-4 w-4 mr-1"/> {editingRecipe ? 'Atualizar receita' : 'Guardar receita'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingRecipe ? 'Atualizar receita' : 'Guardar receita'}</DialogTitle>
                  <DialogDescription>Dê à receita um nome claro para a encontrar mais tarde.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="recipe-name">Nome</Label>
                    <Input id="recipe-name" data-testid="save-recipe-name-input" value={recipeName} onChange={e => setRecipeName(e.target.value)} placeholder="ex.: CRM filc Rex 50ES manutenções"/>
                  </div>
                  <div>
                    <Label htmlFor="recipe-url">URL inicial</Label>
                    <Input id="recipe-url" value={url} onChange={e => setUrl(e.target.value)} className="mono text-sm"/>
                  </div>
                  <div>
                    <Label htmlFor="recipe-notes">Notas (opcional)</Label>
                    <Textarea id="recipe-notes" value={recipeNotes} onChange={e => setRecipeNotes(e.target.value)} placeholder="Algo útil para recordar…" rows={3}/>
                  </div>
                  <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 px-3 py-2">
                    <div>
                      <Label className="text-sm">Converter tempos para minutos</Label>
                      <p className="text-xs text-muted-foreground">Converte automaticamente colunas no formato HH:MM:SS para minutos decimais.</p>
                    </div>
                    <Switch checked={convertTimes} onCheckedChange={setConvertTimes} data-testid="recipe-convert-times-switch"/>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancelar</Button>
                  <Button onClick={onSave} disabled={busy} data-testid="save-recipe-modal-submit-button">{busy ? 'A guardar…' : (editingRecipe ? 'Atualizar' : 'Guardar receita')}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Dica contextual conforme o modo seleccionado */}
        {sessionId && MODE_HINTS[mode] && (
          <div className={`rounded-lg border px-3 py-2 text-xs flex items-start gap-2 ${MODE_HINTS[mode].color}`} data-testid="recorder-mode-hint">
            <div className="font-semibold shrink-0">{MODE_HINTS[mode].title}:</div>
            <div className="text-muted-foreground">{MODE_HINTS[mode].body}</div>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5">
        <div className="space-y-3">
          <RecorderCanvas screenshotUrl={screenshot} mode={mode} steps={steps} onCanvasClick={onCanvasClick} busy={busy}/>
          {previewRows && (
            <Card data-testid="extract-preview" className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold">Pré-visualização da última extração</div>
                <div className="text-xs text-muted-foreground">{previewRows.rows?.length || 0} linhas · {previewRows.headers?.length || 0} colunas</div>
              </div>
              <div className="max-h-64 overflow-auto border border-border rounded-lg scrollbar-thin">
                <table className="w-full text-xs">
                  <thead className="bg-muted sticky top-0">
                    <tr>{(previewRows.headers || []).map((h,i)=>(<th key={i} className="text-left px-2 py-1.5 border-b border-border whitespace-nowrap">{h}</th>))}</tr>
                  </thead>
                  <tbody>
                    {(previewRows.rows || []).slice(0,30).map((r,ri)=>(
                      <tr key={ri} className="hover:bg-muted/40">
                        {r.map((c,ci)=>(<td key={ci} className="px-2 py-1.5 border-b border-border align-top whitespace-pre-wrap mono">{c}</td>))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(previewRows.rows || []).length > 30 && (
                <div className="text-xs text-muted-foreground mt-2">A mostrar as primeiras 30 linhas. Guarde a receita e corra-a para ver o resultado completo.</div>
              )}
            </Card>
          )}
        </div>
        <StepsTimeline steps={steps} onUpdate={onUpdateStep} onDelete={onDeleteStep} onDeleteLast={onUndo} onMove={onMoveStep} onAdd={onAddStep}/>
      </div>

      {/* Fill dialog */}
      <Dialog open={fillOpen} onOpenChange={setFillOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Escrever um valor</DialogTitle>
            <DialogDescription>Este valor vai ser escrito no campo em que acabou de clicar.</DialogDescription>
          </DialogHeader>
          <Input data-testid="fill-value-input" autoFocus value={fillValue} onChange={e => setFillValue(e.target.value)} placeholder="valor…" onKeyDown={(e) => { if (e.key==='Enter') onFillConfirm(); }}/>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFillOpen(false)}>Cancelar</Button>
            <Button onClick={onFillConfirm} data-testid="fill-value-confirm-button">Escrever</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tour interativo (1.ª visita ou pelo botão "Tour interativo" no painel) */}
      <RecorderTour steps={TOUR_STEPS} open={tourOpen} onClose={closeTour}/>
    </div>
  );
}
